"use client";

import { useState } from "react";
import { ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { trackOnboardingEventOnce } from "@/lib/analytics/onboardingFunnel";
import { getAuthRedirect } from "@/lib/authRedirect";
import { ADULT_ELIGIBILITY_PARAM, ADULT_ELIGIBILITY_VALUE } from "@/lib/sharedEligibility";
import { sanitizeNextPath } from "@/lib/sanitizeNextPath";
import { getSupabaseClient } from "@/lib/supabase/client";
import { useNewUniversity } from "@/lib/university/UniversityProvider";
import { getOptionalUniversityConfig } from "@/lib/university/universities";
import type { LearnerStatus } from "@/lib/auth/ukStudentProfile";

interface GoogleSignInButtonProps {
    agree?: boolean;
    displayName?: string;
    yearGroup?: "foundation" | "year1" | "year2" | "year3";
    mode?: "login" | "signup";
    entryIntent?: string;
    next?: string;
    onBeforeSignIn?: () => void;
    onError?: (message: string) => void;
    universityKey?: string;
    /** Shared study only: the student ticked the 18+ statement on this page. */
    adultConfirmed?: boolean;
    studyUniversity?: string;
    learnerStatus?: LearnerStatus;
}

export default function GoogleSignInButton({
    agree = true,
    displayName,
    yearGroup,
    mode = "login",
    entryIntent,
    next,
    onBeforeSignIn,
    onError,
    universityKey,
    adultConfirmed = false,
    studyUniversity,
    learnerStatus,
}: GoogleSignInButtonProps) {
    const [loading, setLoading] = useState(false);
    const { uni } = useNewUniversity();
    const selectedUniversity = getOptionalUniversityConfig(universityKey) || uni;
    const sharedStudy = entryIntent === "shared-study";
    const safeNext = sanitizeNextPath(next, "");

    const handleGoogleSignIn = async () => {
        onBeforeSignIn?.();
        trackOnboardingEventOnce("google_oauth_initiated", {
            university: sharedStudy ? "shared" : selectedUniversity.key,
        });
        setLoading(true);

        try {
            const supabase = getSupabaseClient();
            const redirectTo = getAuthRedirect({
                params: {
                    ...(entryIntent === 'independent-study' || sharedStudy ? {} : { university: selectedUniversity.key }),
                    provider: "google",
                    ...(sharedStudy ? { terms: "accepted" } : {}),
                    ...(sharedStudy && adultConfirmed ? { [ADULT_ELIGIBILITY_PARAM]: ADULT_ELIGIBILITY_VALUE } : {}),
                    ...(sharedStudy && studyUniversity ? { study_university: studyUniversity } : {}),
                    ...(sharedStudy && learnerStatus ? { learner_status: learnerStatus } : {}),
                    ...(entryIntent ? { intent: entryIntent } : {}),
                    ...(safeNext ? { next: safeNext } : {}),
                },
            });

            if (typeof window !== "undefined" && mode === "signup" && !sharedStudy) {
                const signupMetadata = {
                    display_name: displayName?.trim() || "",
                    year_group: yearGroup || "year1",
                    user_type: yearGroup || "year1",
                    agreed_to_terms: true,
                    selected_university: selectedUniversity.key,
                    auth_provider: "google",
                };

                sessionStorage.setItem(
                    "university_signup_metadata",
                    JSON.stringify(signupMetadata),
                );
                localStorage.setItem(
                    "university_signup_backup",
                    JSON.stringify(signupMetadata),
                );
            }

            const { error } = await supabase.auth.signInWithOAuth({
                provider: "google",
                options: {
                    redirectTo,
                    queryParams: {
                        prompt: "select_account",
                    },
                },
            });

            if (error) {
                throw error;
            }
        } catch {
            onError?.("Sign-in was not completed. Please try again.");
            setLoading(false);
        }
    };

    return (
        <Button
            onClick={handleGoogleSignIn}
            className="group relative w-full overflow-hidden rounded-[22px] border border-slate-200 bg-white px-5 py-4 text-base font-semibold text-slate-950 shadow-[0_22px_45px_-28px_rgba(15,23,42,0.35)] transition duration-200 hover:-translate-y-0.5 hover:border-emerald-300 hover:bg-slate-50 hover:shadow-[0_26px_60px_-26px_rgba(15,118,110,0.35)] disabled:cursor-not-allowed disabled:opacity-50"
            disabled={loading || !agree || (mode === "signup" && !sharedStudy && (!displayName?.trim() || !yearGroup))}
        >
            {loading ? (
                <span className="flex items-center justify-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-b-slate-900" />
                    <span>Connecting to Google...</span>
                </span>
            ) : (
                <span className="flex items-center justify-between gap-4 text-left">
                    <span className="flex items-center gap-3">
                        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm">
                            <svg className="h-5 w-5" viewBox="0 0 48 48" aria-hidden="true">
                                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.6 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12S17.4 12 24 12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
                                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 18.9 12 24 12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C34.1 6.1 29.3 4 24 4c-7.7 0-14.3 4.3-17.7 10.7z" />
                                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.2-7.9l-6.6 5.1C9.5 39.6 16.2 44 24 44z" />
                                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.3 5.6l.1-.1 6.2 5.2C36.9 39 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
                            </svg>
                        </span>
                        <span className="block">Continue with Google</span>
                    </span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:text-emerald-700" />
                </span>
            )}
        </Button>
    );
}
